export const LIFECYCLE_KINDS = ["entry_confirmation", "winner", "non_winner"] as const;
export type LifecycleKind = (typeof LIFECYCLE_KINDS)[number];

export const WEBHOOK_EVENTS = ["entry.created", "entry.unsubscribed", "giveaway.completed"] as const;
export type WebhookEventName = (typeof WEBHOOK_EVENTS)[number];

export const KNOWN_MERGE_TOKENS = [
  "first_name",
  "giveaway_name",
  "prize",
  "draw_date",
  "tenant_name",
  "offer_text",
  "offer_expiry",
  "unsubscribe_url",
  "postal_address",
] as const;

const OFFER_TOKENS = ["offer_text", "offer_expiry"] as const;

export const CORE_PAYLOAD_KEYS = [
  "event",
  "external_id",
  "business_id",
  "list_key",
  "captured_at",
  "capture_source",
  "email",
  "first_name",
  "last_name",
  "phone",
  "consent_email",
  "consent_sms",
  "consent_text",
  "custom_fields",
] as const;

/** Stored in organizations.email_reply_to when the tenant chooses no reply address. */
export const REPLY_TO_NONE = "__none__";

export const TEMPLATE_SEEDS: Record<LifecycleKind, { subject: string; body: string }> = {
  entry_confirmation: {
    subject: "You're entered in {{giveaway_name}}",
    body: "Hi {{first_name}},\n\nThis confirms your entry in {{giveaway_name}}. We'll draw the winner on {{draw_date}}.\n\nPrize: {{prize}}.",
  },
  winner: {
    subject: "You won {{giveaway_name}}",
    body: "Hi {{first_name}},\n\nYou won {{prize}} in {{giveaway_name}}. Reply to this email and we'll tell you how to claim it.",
  },
  non_winner: {
    subject: "Thanks for entering {{giveaway_name}}",
    body: "Hi {{first_name}},\n\nThanks for entering {{giveaway_name}}. Someone else won {{prize}} this time.\n\nThis offer expires {{offer_expiry}}.",
  },
};

const TOKEN_RE = /\{\{\s*([a-z0-9_]+)\s*\}\}/g;

export function defaultConsentHelper(sponsorName?: string | null): string {
  const sponsor = sponsorName?.trim() || "the sponsor's";
  const possessive = sponsor === "the sponsor's" ? sponsor : `${sponsor}'s`;
  return `By subscribing you agree to receive marketing emails and ${possessive} Privacy Policy. You can unsubscribe at any time.`;
}

export function composeConsentSnapshot(
  label: string | null | undefined,
  helper: string | null | undefined,
  sponsorName: string | null | undefined,
): string {
  const resolvedHelper = helper?.trim() || defaultConsentHelper(sponsorName);
  if (!label?.trim()) return resolvedHelper;
  return `${label.trim()}\n${resolvedHelper}`;
}

export function splitName(name: string | null | undefined): { first_name: string; last_name: string | null } {
  const trimmed = (name || "").trim();
  if (!trimmed) return { first_name: "", last_name: null };
  const parts = trimmed.split(/\s+/);
  return {
    first_name: parts[0],
    last_name: parts.length > 1 ? parts.slice(1).join(" ") : null,
  };
}

export function resolveParticipantName(input: {
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}): { first_name: string; last_name: string | null } {
  const storedFirst = input.firstName?.trim() || "";
  if (storedFirst) {
    return { first_name: storedFirst, last_name: input.lastName?.trim() || null };
  }
  return splitName(input.name);
}

export function normalizeCaptureSource(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim().toLowerCase();
  if (!normalized || normalized.length > 64) return null;
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(normalized)) return null;
  return normalized;
}

export function applyMerge(
  template: string,
  values: Record<string, string | null | undefined>,
  options?: { stripTokens?: string[] },
): { text: string; errors: string[] } {
  const errors: string[] = [];
  const strip = new Set(options?.stripTokens ?? []);
  const text = template.replace(TOKEN_RE, (match, name: string) => {
    if (!KNOWN_MERGE_TOKENS.includes(name as (typeof KNOWN_MERGE_TOKENS)[number])) {
      errors.push(`Unknown merge token {{${name}}}`);
      return match;
    }
    if (strip.has(name)) return "";
    const value = values[name];
    if (value == null || String(value).trim() === "") {
      errors.push(`Missing value for {{${name}}}`);
      return match;
    }
    return String(value);
  });
  return { text: text.replace(/\n{3,}/g, "\n\n"), errors: [...new Set(errors)] };
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function formatDrawDate(value: string | null | undefined): string {
  if (!value?.trim()) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value.trim();
  return parsed.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" });
}

function sanitizeColor(value: unknown, fallback = "#111827"): string {
  if (typeof value === "string" && /^#[0-9a-fA-F]{3,8}$/.test(value.trim())) return value.trim();
  return fallback;
}

function sanitizeHttpsUrl(value: string | null | undefined): string | null {
  if (!value?.trim()) return null;
  try {
    const url = new URL(value.trim());
    if (url.protocol !== "https:") return null;
    return url.toString();
  } catch {
    return null;
  }
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function isEmailAddress(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function sanitizeMailto(raw: string): string | null {
  let rest = raw.trim().replace(/^mailto:\s*/i, "").replace(/^\/+/, "").trim();
  const queryAt = rest.indexOf("?");
  const query = queryAt >= 0 ? rest.slice(queryAt + 1).trim() : "";
  const addressPart = safeDecode(queryAt >= 0 ? rest.slice(0, queryAt) : rest).trim();
  const bracket = addressPart.match(/<([^<>]+)>/);
  const address = (bracket?.[1] || addressPart).trim();
  if (!isEmailAddress(address)) return null;
  return query ? `mailto:${address}?${query.replace(/\s/g, "%20")}` : `mailto:${address}`;
}

function sanitizePhoneLink(raw: string, protocol: "tel:" | "sms:"): string | null {
  let rest = raw.trim().replace(new RegExp(`^${protocol}\\s*`, "i"), "").replace(/^\/+/, "").trim();
  const queryAt = rest.indexOf("?");
  const query = queryAt >= 0 ? rest.slice(queryAt + 1).trim() : "";
  const number = safeDecode(queryAt >= 0 ? rest.slice(0, queryAt) : rest).replace(/[^\d+().\-\s]/g, "").trim();
  if (!/^\+?[0-9().\-\s]{3,}$/.test(number)) return null;
  const compact = number.replace(/\s/g, "");
  return query ? `${protocol}${compact}?${query.replace(/\s/g, "%20")}` : `${protocol}${compact}`;
}

export function sanitizeOfferUrl(value: string | null | undefined): string | null {
  const raw = value?.trim();
  if (!raw) return null;
  const lower = raw.toLowerCase();
  if (lower.startsWith("mailto:")) return sanitizeMailto(raw);
  if (lower.startsWith("tel:")) return sanitizePhoneLink(raw, "tel:");
  if (lower.startsWith("sms:")) return sanitizePhoneLink(raw, "sms:");
  try {
    const url = new URL(raw);
    if (url.protocol === "https:") return url.toString();
  } catch {
    return null;
  }
  return null;
}

export type RenderedLifecycleEmail = {
  subject: string;
  html: string;
  errors: string[];
};

export function renderLifecycleEmail(input: {
  kind: LifecycleKind;
  subject: string;
  body: string;
  offerText?: string | null;
  offerUrl?: string | null;
  values: Record<string, string | null | undefined>;
  includeOffer: boolean;
  fromName: string;
  accentColor?: string | null;
  buttonTextColor?: string | null;
  logoUrl?: string | null;
}): RenderedLifecycleEmail {
  const strip = input.includeOffer ? [] : [...OFFER_TOKENS];
  const subjectResult = applyMerge(input.subject, input.values, { stripTokens: strip });
  const bodyResult = applyMerge(input.body, input.values, { stripTokens: strip });
  const errors = [...subjectResult.errors, ...bodyResult.errors];

  const paragraphs = bodyResult.text
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block) => `<p style="margin:0 0 16px;line-height:1.5;">${escapeHtml(block).replace(/\n/g, "<br>")}</p>`)
    .join("");

  const offerText = input.includeOffer ? (input.offerText || "").trim() : "";
  const rawOfferUrl = input.includeOffer ? (input.offerUrl || "").trim() : "";
  const offerUrl = rawOfferUrl ? sanitizeOfferUrl(rawOfferUrl) : null;
  if (rawOfferUrl && !offerUrl) errors.push("Offer link must be an https, mailto, tel, or sms link");
  const offerAlreadyInBody = /\{\{\s*offer_text\s*\}\}/.test(input.body) || (offerText && bodyResult.text.includes(offerText));
  const buttonBackground = sanitizeColor(input.accentColor);
  const buttonText = sanitizeColor(input.buttonTextColor, "#ffffff");
  const offerButton = offerUrl && offerText
    ? `<p style="margin:0 0 16px;"><a href="${escapeHtml(offerUrl)}" style="display:inline-block;background:${buttonBackground};color:${buttonText};text-decoration:none;padding:12px 18px;border-radius:6px;">${escapeHtml(offerText)}</a></p>`
    : "";
  const offerBlock = offerButton
    || (offerText && !offerAlreadyInBody
      ? `<p style="margin:0 0 16px;line-height:1.5;">${escapeHtml(offerText)}</p>`
      : "");

  const logo = sanitizeHttpsUrl(input.logoUrl);
  const postal = (input.values.postal_address || "").trim();
  const unsub = (input.values.unsubscribe_url || "").trim();
  if (!postal) errors.push("Missing postal address");
  if (!unsub) errors.push("Missing unsubscribe URL");

  const html = `<!doctype html>
<html>
<body style="margin:0;padding:0;background:#f6f6f6;">
  <div style="max-width:560px;margin:0 auto;padding:24px;font-family:Georgia,serif;color:#111827;">
    <div style="background:#ffffff;border-top:4px solid ${buttonBackground};padding:28px;">
      ${logo ? `<img src="${escapeHtml(logo)}" alt="" width="120" style="display:block;margin:0 0 20px;max-width:120px;height:auto;">` : ""}
      ${paragraphs}
      ${offerBlock}
      <hr style="border:none;border-top:1px solid #e5e7eb;margin:24px 0;">
      <p style="margin:0;font-family:Arial,sans-serif;font-size:12px;line-height:1.5;color:#6b7280;">
        ${escapeHtml(input.fromName)}<br>
        ${postal ? escapeHtml(postal) : "Postal address required"}<br>
        ${unsub ? `<a href="${escapeHtml(unsub)}" style="color:#6b7280;">Unsubscribe</a>` : "Unsubscribe link required"}
      </p>
    </div>
  </div>
</body>
</html>`;

  return {
    subject: subjectResult.text.trim(),
    html,
    errors: [...new Set(errors)],
  };
}

export function buildEntryCreatedPayload(input: {
  participantId: string;
  businessId: string;
  listKey: string;
  capturedAt: string;
  captureSource: string | null;
  email: string;
  name: string | null;
  firstName?: string | null;
  lastName?: string | null;
  phone: string | null;
  consentEmail: boolean;
  consentSms: boolean;
  consentText: string | null;
  customFields: Record<string, string | null>;
  fieldMap: Record<string, string> | null | undefined;
}): Record<string, unknown> {
  const { first_name, last_name } = resolveParticipantName({
    name: input.name,
    firstName: input.firstName,
    lastName: input.lastName,
  });
  const customFields: Record<string, string | null> = {};
  for (const [key, value] of Object.entries(input.customFields || {})) {
    customFields[key] = value == null || String(value).trim() === "" ? null : String(value);
  }

  const payload: Record<string, unknown> = {
    event: "entry.created",
    external_id: input.participantId,
    business_id: input.businessId,
    list_key: input.listKey,
    captured_at: input.capturedAt,
    capture_source: input.captureSource,
    email: input.email,
    first_name,
    last_name,
    phone: input.phone?.trim() ? input.phone.trim() : null,
    consent_email: input.consentEmail,
    consent_sms: input.consentSms,
    consent_text: input.consentText,
    custom_fields: customFields,
  };

  const reserved = new Set<string>(CORE_PAYLOAD_KEYS);
  for (const [fieldKey, exportNameRaw] of Object.entries(input.fieldMap || {})) {
    const exportName = exportNameRaw.trim();
    if (!exportName || reserved.has(exportName) || !/^[a-z][a-z0-9_]*$/.test(exportName)) continue;
    const raw = customFields[fieldKey];
    payload[exportName] = raw == null || String(raw).trim() === "" ? null : raw;
  }

  return payload;
}

export function payloadsToCsv(rows: Record<string, unknown>[]): string {
  const extras = new Set<string>();
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (key !== "custom_fields" && !CORE_PAYLOAD_KEYS.includes(key as (typeof CORE_PAYLOAD_KEYS)[number])) {
        extras.add(key);
      }
    }
  }
  const headers = [
    ...CORE_PAYLOAD_KEYS.filter((key) => key !== "custom_fields"),
    ...[...extras].sort(),
    "custom_fields",
  ];
  const lines = [headers.map(escapeCsv).join(",")];
  for (const row of rows) {
    lines.push(headers.map((header) => {
      const value = row[header];
      if (header === "custom_fields") return escapeCsv(JSON.stringify(value ?? {}));
      if (value == null) return "";
      return escapeCsv(value);
    }).join(","));
  }
  return lines.join("\n");
}

export function escapeCsv(value: unknown): string {
  if (value == null) return "";
  const str = String(value);
  if (/[",\n\r]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
}

export function buildFromAddress(input: {
  organizationName: string;
  sponsorName?: string | null;
  emailFromName?: string | null;
  emailReplyTo?: string | null;
  supportEmail?: string | null;
  sponsorAddress?: string | null;
  sendingDomain?: string | null;
  sendingDomainStatus?: string | null;
}): { from: string; replyTo: string | null; blockReason: "missing_postal_address" | "unverified_domain" | null } {
  if (!input.sponsorAddress?.trim()) {
    return { from: "", replyTo: null, blockReason: "missing_postal_address" };
  }
  const domain = input.sendingDomain?.trim().toLowerCase() || "";
  if (domain && input.sendingDomainStatus !== "verified") {
    return { from: "", replyTo: null, blockReason: "unverified_domain" };
  }
  const rawName = input.emailFromName?.trim() || input.sponsorName?.trim() || input.organizationName?.trim() || "Giveaway";
  const fromName = rawName.replace(/[<>"\r\n]/g, "").trim() || "Giveaway";
  const fromEmail = domain ? `hello@${domain}` : "hello@send.rafflekit.app";
  const requested = input.emailReplyTo?.trim() || "";
  const replyTo = requested === REPLY_TO_NONE ? null : (requested || input.supportEmail?.trim() || null);
  return { from: `${fromName} <${fromEmail}>`, replyTo, blockReason: null };
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function base64UrlToBytes(value: string): Uint8Array {
  const padded = value.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((value.length + 3) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

async function hmacKey(secret: string) {
  return crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
}

export async function hmacSha256Hex(secret: string, message: string): Promise<string> {
  const signature = await crypto.subtle.sign("HMAC", await hmacKey(secret), new TextEncoder().encode(message));
  return [...new Uint8Array(signature)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

export type UnsubscribeClaims = {
  organizationId: string;
  email: string;
  participantId: string;
  contestId: string;
};

export async function signUnsubscribeToken(claims: UnsubscribeClaims, secret: string): Promise<string> {
  const body = bytesToBase64Url(new TextEncoder().encode(JSON.stringify(claims)));
  const signature = await crypto.subtle.sign("HMAC", await hmacKey(secret), new TextEncoder().encode(body));
  return `${body}.${bytesToBase64Url(new Uint8Array(signature))}`;
}

export async function verifyUnsubscribeToken(token: string, secret: string): Promise<UnsubscribeClaims | null> {
  const [body, signature] = token.split(".");
  if (!body || !signature) return null;
  const expected = new Uint8Array(await crypto.subtle.sign("HMAC", await hmacKey(secret), new TextEncoder().encode(body)));
  const actual = base64UrlToBytes(signature);
  if (actual.length !== expected.length) return null;
  let mismatch = 0;
  for (let i = 0; i < actual.length; i++) mismatch |= actual[i] ^ expected[i];
  if (mismatch !== 0) return null;
  try {
    const claims = JSON.parse(new TextDecoder().decode(base64UrlToBytes(body))) as UnsubscribeClaims;
    if (!claims.organizationId || !claims.email || !claims.participantId || !claims.contestId) return null;
    return claims;
  } catch {
    return null;
  }
}

export function unsubscribeLink(supabaseUrl: string, token: string): string {
  return `${supabaseUrl.replace(/\/$/, "")}/functions/v1/unsubscribe?token=${encodeURIComponent(token)}`;
}

export function backoffMinutes(attempts: number): number {
  return Math.min(60, 2 ** Math.max(attempts, 1));
}
